import type { ColumnDef } from "./types";

/**
 * Column registry — single source of truth for `/recap` (sales) and
 * `/purchases` table columns. Visibility is controlled by mode, viewMode,
 * and feature flags. Components never check flags directly; they use
 * resolved columns from useRecapColumns.
 *
 * Transport (`/recap_transport`) has its own registry in
 * `recap_transport/config/columns.ts` — additions here never widen the
 * transport surface, and vice versa.
 */
export const COLUMN_REGISTRY: ColumnDef[] = [
  {
    id: "select",
    headerKey: "",
    width: "14px",
    modes: ["sales", "purchases"],
    align: "center",
    resizable: false,
    hideInTotals: true,
  },
  {
    id: "product",
    headerKey: "ordersRecap.product",
    width: "234px",
    minWidth: "120px",
    modes: ["sales", "purchases"],
  },
  {
    id: "image",
    headerKey: "products.image",
    width: "50px",
    modes: ["sales", "purchases"],
    featureFlag: "showProductImages",
    // showProductImages defaults to true (shown unless explicitly set to false)
  },
  {
    id: "quantity",
    headerKey: "ordersRecap.quantity",
    width: "45px",
    modes: ["sales", "purchases"],
    align: "left",
  },
  {
    id: "unit",
    headerKey: "ordersRecap.unit",
    width: "65px",
    modes: ["sales", "purchases"],
    featureFlag: "showUnitRecap",
    // showUnitRecap defaults to true (shown unless explicitly set to false)
  },
  {
    id: "weight",
    headerKey: "ordersRecap.weight",
    width: "65px",
    modes: ["sales", "purchases"],
    align: "left",
    featureFlag: "showWeightRecap",
    // showWeightRecap defaults to true (shown unless explicitly set to false)
  },
  {
    id: "price",
    headerKey: "ordersRecap.pricePerKg",
    width: "95px",
    modes: ["sales", "purchases"],
    align: "left",
    featureFlag: "showPricesRecap",
  },
  {
    id: "total",
    headerKey: "ordersRecap.total",
    width: "80px",
    modes: ["sales", "purchases"],
    align: "left",
    featureFlag: "showPricesRecap",
  },
  {
    id: "comment",
    headerKey: "ordersRecap.comment",
    width: "90px",
    modes: ["sales", "purchases"],
  },
  {
    id: "custom_fields",
    headerKey: "ordersRecap.customFields",
    width: "100px",
    modes: ["sales", "purchases"],
    align: "center",
    // Visibility in useRecapColumns: shown as a popover fallback on narrow
    // screens; replaced by inline columns on wide screens. Array-shape flag
    // `preOrderFlowCustomFields` is gated explicitly in useRecapColumns.
  },
  {
    id: "status",
    headerKey: "ordersRecap.statusHeader",
    // Just enough for the colored dot/pill — reclaimed space went to the
    // product column (most-scanned).
    width: "36px",
    modes: ["sales", "purchases"],
    resizable: false,
  },
  {
    id: "partner",
    headerKey: "ordersRecap.partner",
    width: "140px",
    modes: ["sales", "purchases"],
    viewModes: ["per-product"],
  },
  {
    id: "order_number",
    headerKey: "ordersRecap.invoice",
    width: "100px",
    modes: ["sales", "purchases"],
    viewModes: ["per-product"],
    featureFlag: "hidePreOrderNumber",
    featureFlagInverse: true,
  },
  {
    id: "actions",
    headerKey: "",
    width: "40px",
    modes: ["sales", "purchases"],
    hideInTotals: true,
    resizable: false,
  },
];

/**
 * Default columns for standalone usage (e.g. chat pre-order divider).
 * Minimal set: product, quantity, weight, price, status, actions.
 */
export const STANDALONE_COLUMNS: ColumnDef[] = COLUMN_REGISTRY.filter((col) =>
  ["product", "quantity", "weight", "price", "status", "actions"].includes(
    col.id,
  ),
);

/** Default column widths by column ID (pixels) */
export const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  select: 14,
  product: 234,
  image: 50,
  quantity: 45,
  unit: 65,
  weight: 65,
  price: 95,
  total: 80,
  comment: 90,
  status: 36,
  partner: 140,
  order_number: 100,
  actions: 40,
  custom_fields: 100,
};
